import {
	Combobox,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxList,
	ComboboxTrigger,
	ComboboxValue,
	DatePicker,
	FILTER_CONTROL_HEIGHT_CLASS,
	FilterBar,
	FilterClearButton,
	FilterField,
	Input,
	useComboboxAnchor,
} from "@components";
import {
	styles as buttonStyles,
	Button as UntitledButton,
} from "@components/button/button-untitledui";
import { CalendarDate, getLocalTimeZone, today } from "@internationalized/date";
import { Download01 } from "@untitledui/icons";
import { useLayoutEffect, useMemo, useRef } from "react";
import type { DateValue } from "react-aria";
import { cx } from "@/utils/cx";
import { EVENT_TYPE_FILTER_OPTIONS } from "../constants/events";
import type { EventTypeCode } from "../types/events";

function stringToDateValue(str: string | null): CalendarDate | null {
	if (!str) {
		return null;
	}
	const [y, m, d] = str.split("-").map(Number);
	if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) {
		return null;
	}
	return new CalendarDate(y, m, d);
}

export type EventsFiltersState = {
	search: string;
	types?: EventTypeCode[];
	startDate: DateValue;
	endDate: DateValue;
};

export function createDefaultEventsFilters(): EventsFiltersState {
	const now = today(getLocalTimeZone());
	return {
		search: "",
		types: undefined,
		startDate: now,
		endDate: now,
	};
}

function filtersAreDirty(
	current: EventsFiltersState,
	baseline: EventsFiltersState
): boolean {
	const sameSearch =
		(current.search ?? "").trim() === (baseline.search ?? "").trim();
	const sameTypes =
		JSON.stringify(current.types ?? []) ===
		JSON.stringify(baseline.types ?? []);
	const sameStart =
		current.startDate.toString() === baseline.startDate.toString();
	const sameEnd = current.endDate.toString() === baseline.endDate.toString();

	return !(sameSearch && sameTypes && sameStart && sameEnd);
}

type EventTypeSelectItem = { id: EventTypeCode; label: string };

const SEARCH_INPUT_ID = "events-search";

function normalizeEventTypeQuery(value: string): string {
	return value.trim().toLowerCase();
}

type EventsFiltersProps = {
	filters: EventsFiltersState;
	onFiltersChange: (f: EventsFiltersState) => void;
	onPageChange: (page: number) => void;
	searchInput: string;
	onSearchInputChange: (value: string) => void;
	hasListData?: boolean;
	onExport: () => void;
};

export function EventsFilters({
	filters,
	onFiltersChange,
	onPageChange,
	searchInput,
	onSearchInputChange,
	hasListData = false,
	onExport,
}: EventsFiltersProps) {
	const anchor = useComboboxAnchor();

	const baselineRef = useRef<EventsFiltersState | null>(null);
	const filtersOnMountRef = useRef(filters);
	filtersOnMountRef.current = filters;

	useLayoutEffect(() => {
		if (baselineRef.current !== null) {
			return;
		}
		const initial = filtersOnMountRef.current;
		baselineRef.current = {
			search: initial.search,
			types: initial.types,
			startDate: initial.startDate,
			endDate: initial.endDate,
		};
	}, []);

	const hasActiveFilters = baselineRef.current
		? filtersAreDirty(filters, baselineRef.current)
		: false;

	const shouldShowClearFiltersLink = hasListData && hasActiveFilters;

	const typeItems = useMemo<EventTypeSelectItem[]>(
		() => EVENT_TYPE_FILTER_OPTIONS.map((o) => ({ id: o.id, label: o.label })),
		[]
	);

	const selectedTypes = useMemo(() => {
		if (!filters.types?.length) {
			return [];
		}

		return filters.types
			.map((id) => typeItems.find((item) => item.id === id))
			.filter((item): item is EventTypeSelectItem => item !== undefined);
	}, [filters.types, typeItems]);

	const filtersRef = useRef(filters);
	filtersRef.current = filters;

	const update = (patch: Partial<EventsFiltersState>) => {
		onFiltersChange({ ...filtersRef.current, ...patch });
		onPageChange(1);
	};

	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		onSearchInputChange(e.target.value);
	};

	const handleClearFilters = () => {
		const baseline = baselineRef.current;
		if (!baseline) {
			return;
		}

		onSearchInputChange(baseline.search ?? "");
		onFiltersChange({
			search: baseline.search,
			types: baseline.types,
			startDate: baseline.startDate,
			endDate: baseline.endDate,
		});
		onPageChange(1);
	};

	return (
		<FilterBar
			actions={
				<>
					{shouldShowClearFiltersLink ? (
						<FilterClearButton onClick={handleClearFilters} />
					) : null}
					<UntitledButton
						aria-label="Exportar eventos"
						color="secondary"
						iconLeading={Download01}
						onClick={onExport}
						size="sm"
						type="button"
					>
						Exportar
					</UntitledButton>
				</>
			}
			className="mb-0"
			label="Filtros de eventos"
		>
			<FilterField label="Data inicial">
				<DatePicker
					aria-label="Data inicial"
					className="w-full"
					maxValue={filters.endDate}
					onChange={(date) => {
						if (!date) {
							return;
						}
						update({ startDate: date });
					}}
					size="sm"
					value={stringToDateValue(
						filters.startDate ? filters.startDate.toString() : null
					)}
				/>
			</FilterField>

			<FilterField label="Data final">
				<DatePicker
					aria-label="Data final"
					className="w-full"
					maxValue={today(getLocalTimeZone())}
					minValue={filters.startDate}
					onChange={(date) => {
						if (!date) {
							return;
						}
						update({ endDate: date });
					}}
					size="sm"
					value={stringToDateValue(
						filters.endDate ? filters.endDate.toString() : null
					)}
				/>
			</FilterField>

			<FilterField label="Tipo de evento">
				<div className="w-full" ref={anchor}>
					<Combobox
						autoHighlight
						filter={(item: EventTypeSelectItem, query: string) => {
							const q = normalizeEventTypeQuery(query);
							if (!q) {
								return true;
							}
							const label = (item.label ?? "").toLowerCase();
							return (
								label.includes(q) || String(item.id).toLowerCase().includes(q)
							);
						}}
						items={typeItems}
						itemToStringValue={(item: EventTypeSelectItem) => String(item.id)}
						multiple
						onValueChange={(fieldValue) => {
							let nextTypes: EventTypeCode[] | undefined;
							if (fieldValue.length > 0) {
								nextTypes = fieldValue.map((item) => item.id);
							} else {
								nextTypes = undefined;
							}
							update({ types: nextTypes });
						}}
						value={selectedTypes}
					>
						<ComboboxTrigger
							render={
								<button
									aria-label="Filtrar por tipo de evento"
									className={cx(
										buttonStyles.common.root,
										buttonStyles.sizes.sm.root,
										buttonStyles.colors.secondary.root,
										`${FILTER_CONTROL_HEIGHT_CLASS} w-full justify-start px-3.5 font-normal text-secondary text-sm`
									)}
									tabIndex={0}
									type="button"
								>
									<span className="w-full max-w-full truncate">
										<ComboboxValue>
											{(cbxState: EventTypeSelectItem[]) => {
												if (cbxState.length === 0) {
													return "Todos os eventos";
												}
												if (cbxState.length === 1) {
													return cbxState[0].label ?? "";
												}
												return `${cbxState.length} tipos`;
											}}
										</ComboboxValue>
									</span>
								</button>
							}
						/>
						<ComboboxContent anchor={anchor} className="w-[280px] bg-white">
							<ComboboxInput
								placeholder="Buscar tipo de evento"
								showTrigger={false}
							/>
							<ComboboxEmpty>Nenhum tipo encontrado.</ComboboxEmpty>
							<ComboboxList>
								{(item) => (
									<ComboboxItem
										className="h-9 min-h-fit text-sm"
										key={item.id}
										value={item}
									>
										{item.label}
									</ComboboxItem>
								)}
							</ComboboxList>
						</ComboboxContent>
					</Combobox>
				</div>
			</FilterField>

			<FilterField
				className="sm:max-w-[600px]"
				grow
				htmlFor={SEARCH_INPUT_ID}
				label="Placa ou motorista"
				labelVisibility="hidden"
			>
				<Input
					className={FILTER_CONTROL_HEIGHT_CLASS}
					id={SEARCH_INPUT_ID}
					onChange={handleSearchChange}
					placeholder="Buscar por placa ou nome"
					value={searchInput}
				/>
			</FilterField>
		</FilterBar>
	);
}
